import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useToast } from '../../context/ToastContext';

// Trocar o tipo de um imóvel JÁ salvo (ex.: cadastrei como Casa, mas é Chácara). Fluxo: escolhe o novo tipo → o
// servidor devolve uma PRÉVIA do que vai mudar (código, características removidas) → o usuário confirma.
// Fotos não são afetadas (a chave no R2 usa o id do imóvel, não o tipo nem o código). Ver painel-node
// `controllers/imovel.js` (trocarTipo) para o raciocínio completo.
export default function ModalTrocarTipo({ show, aoFechar, publicId, tipos, tipoAtual, aoTrocar, trocaTipo, aoCopiar }) {
  const toast = useToast();
  const [tipoId, setTipoId] = useState('');
  const [previa, setPrevia] = useState(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const [novoCodigo, setNovoCodigo] = useState(false);
  const [ciente, setCiente] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // reabre sempre limpo
  useEffect(() => {
    if (show) {
      setTipoId('');
      setPrevia(null);
      setNovoCodigo(false);
      setCiente(false);
    }
  }, [show]);

  async function escolherTipo(id) {
    setTipoId(id);
    setPrevia(null);
    setCiente(false);
    if (!id) return;
    setCarregandoPrevia(true);
    try {
      const r = await api.post(`/imoveis/${publicId}/trocar-tipo/previa`, { tipoId: Number(id) });
      setPrevia(r.data);
      setNovoCodigo(!!r.data.sugerirNovoCodigo);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível calcular a troca');
      setTipoId('');
    } finally {
      setCarregandoPrevia(false);
    }
  }

  async function confirmar() {
    setSalvando(true);
    try {
      const r = await api.post(`/imoveis/${publicId}/trocar-tipo`, { tipoId: Number(tipoId), confirmar: true, novoCodigo });
      toast.sucesso(
        r.resumo?.codigoNovo && r.resumo.codigoNovo !== r.resumo.codigoAnterior
          ? `Tipo alterado. Novo código: ${r.resumo.codigoNovo}.`
          : 'Tipo alterado.'
      );
      aoTrocar(r.data);
      aoFechar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível trocar o tipo');
    } finally {
      setSalvando(false);
    }
  }

  const removidas = previa?.caracteristicasRemovidas || [];

  // Prazo vencido (Configurações → "Prazo para trocar o tipo"): nada para escolher, só explica e oferece o Copiar.
  if (trocaTipo && trocaTipo.permitida === false) {
    return (
      <Modal show={show} onHide={aoFechar} centered>
        <Modal.Header closeButton>
          <Modal.Title>O tipo não pode mais ser trocado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            O tipo de um imóvel só pode ser trocado até <strong>{trocaTipo.janelaHoras} hora{trocaTipo.janelaHoras > 1 ? 's' : ''}</strong> depois do
            cadastro. Esse imóvel já passou do prazo: o código dele (que nasce do tipo) já está em uso em portais, mensagens e leads.
          </p>
          <p className="mb-0">
            Se o tipo está errado, use <strong>Copiar imóvel</strong>: cria um imóvel novo com as mesmas características e o código do tipo
            certo. Depois é só remover o original.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={aoFechar}>Fechar</Button>
          {aoCopiar && (
            <Button variant="primary" onClick={() => { aoFechar(); aoCopiar(); }}>
              <i className="bi bi-files" /> Copiar imóvel
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={salvando ? undefined : aoFechar} centered>
      <Modal.Header closeButton={!salvando}>
        <Modal.Title>Trocar o tipo do imóvel</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="trocar-tipo-novo">
          <Form.Label>Novo tipo</Form.Label>
          <Form.Select value={tipoId} onChange={(e) => escolherTipo(e.target.value)} disabled={salvando}>
            <option value="">Selecione…</option>
            {tipos
              .filter((t) => String(t.id) !== String(tipoAtual))
              .map((t) => (
                <option key={t.id} value={String(t.id)}>{t.descricao}</option>
              ))}
          </Form.Select>
        </Form.Group>

        {carregandoPrevia && (
          <div className="text-center my-3"><Spinner animation="border" size="sm" /> Calculando o que vai mudar…</div>
        )}

        {previa && (
          <>
            <Alert variant="warning" className="small">
              O imóvel passará de <strong>{previa.tipoAtual}</strong> para <strong>{previa.tipoNovo}</strong>. Confira o que muda:
              <ul className="mb-0 mt-2">
                <li>
                  <strong>Fotos:</strong> nada muda — elas ficam guardadas pelo número do imóvel, não pelo tipo nem pelo código.
                </li>
                <li>
                  <strong>Características:</strong>{' '}
                  {removidas.length === 0
                    ? 'todas as atuais continuam valendo no novo tipo.'
                    : <>as que o novo tipo não possui serão <strong>removidas</strong>: {removidas.join(', ')}.</>}
                </li>
                <li>
                  O imóvel será reenviado aos portais/sites com o novo tipo. <strong>Revise</strong> categoria, finalidade e
                  quartos/áreas, que podem não fazer sentido no novo tipo.
                </li>
              </ul>
            </Alert>

            {previa.codigoMuda ? (
              <Form.Group className="mb-3" controlId="trocar-tipo-codigo">
                <Form.Check
                  type="checkbox"
                  label={<>Gerar um <strong>novo código</strong> para o novo tipo: <code>{previa.codigoNovo}</code> (hoje é <code>{previa.codigoAtual}</code>)</>}
                  checked={novoCodigo}
                  onChange={(e) => setNovoCodigo(e.target.checked)}
                  disabled={salvando}
                />
                <Form.Text muted>
                  O código antigo fica registrado na descrição interna do imóvel.
                  {previa.publicado
                    ? ' Atenção: este imóvel já está publicado — trocar o código muda o identificador do anúncio nos portais (pode aparecer como anúncio novo e perder o histórico) e mensagens já enviadas citam o código antigo. Se não tiver certeza, deixe desmarcado.'
                    : ' Como o imóvel ainda não está publicado, é seguro gerar o novo código.'}
                </Form.Text>
              </Form.Group>
            ) : (
              <p className="small text-muted">O código <code>{previa.codigoAtual}</code> já combina com o novo tipo e será mantido.</p>
            )}

            <Form.Check
              id="trocar-tipo-ciente"
              type="checkbox"
              label="Entendi o que vai mudar e quero trocar o tipo"
              checked={ciente}
              onChange={(e) => setCiente(e.target.checked)}
              disabled={salvando}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={aoFechar} disabled={salvando}>Cancelar</Button>
        <Button variant="warning" onClick={confirmar} disabled={!previa || !ciente || salvando}>
          {salvando ? 'Trocando…' : 'Trocar tipo'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
